import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSingleVisualization, subscribePanel, unsubscribePanel } from './actions';

export const useSubscription = (visualizationId, panelId) => {
  const visualizationPanelValues = useSelector(({ visualizations }) =>
    visualizations.values[visualizationId]?.find((v) => v.panelId === panelId)
  );

  const { latestValues, seriesValues, loading, initialLoaded } = visualizationPanelValues || {};
  return {
    latestValues,
    seriesValues,
    loading,
    initialLoaded,
  };
};

// The panel subscription does the first fetch, so only a later change of rangeKey refetches.
export const useRefetchOnRangeChange = ({
  visualization,
  panelId,
  initialLoaded,
  selected,
  rangeKey,
}) => {
  const dispatch = useDispatch();
  const fetchedRangeKey = useRef(rangeKey);

  useEffect(() => {
    if (!initialLoaded || selected || fetchedRangeKey.current === rangeKey) return;
    fetchedRangeKey.current = rangeKey;
    dispatch(getSingleVisualization(visualization, panelId));
  }, [initialLoaded, selected, rangeKey, visualization, panelId, dispatch]);
};

// Panel-level subscription - ONLY way to subscribe visualizations
// Sets up state for all visualizations and triggers batched data fetch
export const usePanelSubscription = (visualizations, panelId) => {
  const dispatch = useDispatch();
  const latestVisualizations = useRef(visualizations);
  latestVisualizations.current = visualizations;

  // Create a key based on visualization IDs to detect when the set changes
  const vizKey =
    visualizations
      ?.filter(Boolean)
      .map((v) => v.id)
      .toSorted()
      .join(',') || '';

  // Keyed on vizKey only: a new array with the same ids must not unsubscribe and refetch.
  useEffect(() => {
    const subscribed = latestVisualizations.current;
    if (subscribed && subscribed.length > 0) {
      // Single subscription for entire panel handles state setup AND data fetching
      dispatch(subscribePanel(subscribed, panelId));
      return () => {
        dispatch(unsubscribePanel(subscribed, panelId));
      };
    }
  }, [panelId, vizKey, dispatch]);
};
